import { useState } from "react";
import { useDelete, useGetList, useNotify, useTranslate } from "ra-core";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Confirm } from "@/components/admin/confirm";

import { TagCreateModal } from "../tags/TagCreateModal";
import { TagEditModal } from "../tags/TagEditModal";
import { TAG_FETCH_LIMIT } from "../tags/useAllTags";
import type { Tag } from "../types";

// PostgREST returns an embedded aggregate as an array holding a single row.
// FakeRest ignores meta.columns, so in demo mode the field is simply absent —
// which is why every read of it goes through usageCount and every render of it
// is conditional. A confident "0 contacts" beside a tag that has 220 would be
// worse than showing no number at all.
type TagWithUsage = Tag & {
  rel__contacts__tags?: { count: number }[];
};

const usageCount = (tag: TagWithUsage) => tag.rel__contacts__tags?.[0]?.count;

export const TagsSection = () => {
  const translate = useTranslate();
  const notify = useNotify();

  // Not useAllTags(): this is the one consumer that needs the usage count, and
  // meta.columns is passed to PostgREST as the select= clause verbatim. Counting
  // client-side would mean fetching the junction table, which already holds 936
  // rows against a max_rows of 1000 and would start truncating silently.
  const { data: tags, refetch } = useGetList<TagWithUsage>("tags", {
    pagination: { page: 1, perPage: TAG_FETCH_LIMIT },
    sort: { field: "name", order: "ASC" },
    meta: { columns: ["id", "name", "color", "rel__contacts__tags(count)"] },
  });

  const [deleteOne, { isPending: isDeleting }] = useDelete();
  const [createOpen, setCreateOpen] = useState(false);
  const [tagToEdit, setTagToEdit] = useState<TagWithUsage | null>(null);
  const [tagToDelete, setTagToDelete] = useState<TagWithUsage | null>(null);

  const deleteCount = tagToDelete ? usageCount(tagToDelete) : undefined;

  // Three cases, because "removes the tag from 0 contacts" reads badly and a
  // missing count must not be rendered as zero. Polyglot only gives singular
  // and plural, so the empty case is its own key.
  const deleteContent =
    deleteCount === undefined
      ? {
          key: "crm.settings.tags.delete_content_unknown",
          options: {
            _: "This removes the tag from every contact that carries it. It cannot be undone.",
          },
        }
      : deleteCount === 0
        ? {
            key: "crm.settings.tags.delete_content_unused",
            options: { _: "No contact carries this tag. It cannot be undone." },
          }
        : {
            key: "crm.settings.tags.delete_content",
            options: {
              _: "This removes the tag from %{smart_count} contact. It cannot be undone. |||| This removes the tag from %{smart_count} contacts. It cannot be undone.",
              smart_count: deleteCount,
            },
          };

  const handleDelete = () => {
    if (!tagToDelete) return;
    deleteOne(
      "tags",
      { id: tagToDelete.id, previousData: tagToDelete },
      {
        onSuccess: () => {
          setTagToDelete(null);
          refetch();
        },
        onError: (error: any) => {
          notify(error?.message || "Failed to delete tag", { type: "error" });
        },
      },
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-muted-foreground">
            {translate("crm.settings.sections.tags", { _: "Tags" })}
          </h2>
          <p className="text-sm text-muted-foreground">
            {translate("crm.settings.tags.description", {
              _: "Tags you create here are available on every contact. Deleting one removes it from the contacts that carry it.",
            })}
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="shrink-0"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-4 w-4 mr-1" />
          {translate("resources.tags.action.create", { _: "Create new tag" })}
        </Button>
      </div>

      <div className="space-y-1">
        {tags?.map((tag) => {
          const count = usageCount(tag);
          return (
            <div
              key={tag.id}
              className="flex items-center justify-between gap-2 py-1 px-2 rounded hover:bg-muted"
            >
              <div className="flex items-center gap-2 min-w-0">
                <Badge
                  variant="secondary"
                  className="text-xs font-normal text-black"
                  style={{ backgroundColor: tag.color }}
                >
                  {tag.name}
                </Badge>
                {count !== undefined && (
                  <span className="text-xs text-muted-foreground">
                    {translate("crm.settings.tags.usage", {
                      smart_count: count,
                      _: "%{smart_count} contact |||| %{smart_count} contacts",
                    })}
                  </span>
                )}
              </div>
              <div className="flex shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={translate("ra.action.edit", { _: "Edit" })}
                  onClick={() => setTagToEdit(tag)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  aria-label={translate("ra.action.delete", { _: "Delete" })}
                  onClick={() => setTagToDelete(tag)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          );
        })}
        {tags?.length === 0 && (
          <p className="text-sm text-muted-foreground italic">
            {translate("crm.settings.tags.empty", {
              _: "No tags yet.",
            })}
          </p>
        )}
      </div>

      <TagCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={async () => {
          setCreateOpen(false);
          await refetch();
        }}
      />

      {tagToEdit && (
        <TagEditModal
          tag={tagToEdit}
          open
          onClose={() => setTagToEdit(null)}
          onSuccess={async () => {
            setTagToEdit(null);
            await refetch();
          }}
        />
      )}

      <Confirm
        isOpen={tagToDelete !== null}
        loading={isDeleting}
        confirmColor="warning"
        title="crm.settings.tags.delete_title"
        titleTranslateOptions={{
          _: 'Delete "%{name}"?',
          name: tagToDelete?.name ?? "",
        }}
        content={deleteContent.key}
        contentTranslateOptions={deleteContent.options}
        onConfirm={handleDelete}
        onClose={() => setTagToDelete(null)}
      />
    </div>
  );
};
